import type { TFunction } from 'i18next';

/**
 * `userErrorMessage` — zentraler, reiner Fehler-Mapper (#32 Welle P1,
 * `plan-ux-toast-wordings.md` §1.1). Wandelt eine beliebige `unknown`
 * Fehler-Instanz (FirebaseError, Axios-artiger Error mit `.response.status`,
 * Netzwerk-`TypeError`, oder ein simpler `Error` mit Dev-facing Message) in
 * einen fertigen, lokalisierten, nutzerfreundlichen Satz um.
 *
 * Gibt **NIEMALS** `err.message` (oder einen Substring davon) zurück — rohe
 * SDK-/Backend-Strings (`auth/...`, `Firebase: ...`, `HTTP 500`, `Request
 * failed with status code ...`) dürfen den Nutzer nie erreichen. Diagnose-
 * Details gehören ausschließlich in `logger.error(err, ...)` an der
 * Call-Site — NICHT hierher.
 *
 * Auflösungsreihenfolge (erste zutreffende Stufe gewinnt):
 * 1. Firebase-Fehlercode (`err.code`) exakt in {@link FIREBASE_CODE_MAP}.
 * 2. HTTP-Status (`err.response?.status`) in {@link HTTP_STATUS_MAP}.
 * 3. Netzwerk-Heuristik (`TypeError` bzw. bekannte Netzwerk-Message-Fragmente).
 * 4. `opts.fallbackKey` — domänenspezifischer Kontext-Fallback der Call-Site
 *    (kann ein bestehender Namespace-Key des Aufrufers sein, z.B.
 *    `'download.error'` in `posteingang.json`, oder ein neuer kuratierter
 *    `errors.*`-Key aus `common.json`).
 * 5. `errors.generic` (immer vorhanden, letzter Fallback).
 *
 * Kuratierte Keys (Stufen 1-3 + 5) leben bewusst im `common`-Namespace
 * (kein neuer i18n-Namespace nötig, `common` ist überall geladen) und werden
 * daher explizit mit `{ ns: 'common' }` aufgelöst — unabhängig davon, an
 * welchen Namespace der Aufrufer-`t` gebunden ist. `opts.fallbackKey`
 * (Stufe 4) wird dagegen OHNE Namespace-Override aufgelöst, damit bereits
 * bestehende, gute Call-Site-Keys aus einem anderen (Domänen-)Namespace
 * weiterverwendet werden können (§2.2 des Plans: "keine Doppel-Anlage").
 *
 * @param err Der gefangene Fehler (beliebiger Typ, meist `unknown` aus einem
 *   `catch`-Block).
 * @param t Die `t`-Funktion des Aufrufers (aus `useTranslation()`), unabhängig
 *   von deren gebundenem Default-Namespace.
 * @param opts.fallbackKey Optionaler, domänenspezifischer i18n-Key (Stufe 4),
 *   der VOR dem generischen Fallback (Stufe 5) versucht wird.
 * @returns Ein fertiger, lokalisierter Satz — nie leer, nie eine rohe
 *   SDK-/HTTP-Message.
 */
export function mapErrorToUserMessage(
  err: unknown,
  t: TFunction,
  opts?: { fallbackKey?: string },
): string {
  const code = extractFirebaseCode(err);
  if (code && code in FIREBASE_CODE_MAP) {
    const resolved = resolveCommonKey(t, FIREBASE_CODE_MAP[code]);
    if (resolved) return resolved;
  }

  const status = extractHttpStatus(err);
  if (status !== undefined) {
    const statusKey = resolveHttpStatusKey(status);
    if (statusKey) {
      const resolved = resolveCommonKey(t, statusKey);
      if (resolved) return resolved;
    }
  }

  if (isNetworkError(err)) {
    const resolved = resolveCommonKey(t, 'errors.network');
    if (resolved) return resolved;
  }

  if (opts?.fallbackKey) {
    const resolved = resolveKey(t, opts.fallbackKey);
    if (resolved) return resolved;
  }

  return resolveCommonKey(t, 'errors.generic') ?? 'Das hat nicht geklappt. Bitte erneut versuchen.';
}

/**
 * Firebase-Fehlercode → kuratierter `common.json`-Key (`errors.*`).
 * `auth/network-request-failed` ist bewusst ein Alias auf `errors.network`
 * (kein eigener Text, §2.1 des Plans).
 */
const FIREBASE_CODE_MAP: Record<string, string> = {
  'auth/invalid-credential': 'errors.auth.invalidCredentials',
  'auth/wrong-password': 'errors.auth.invalidCredentials',
  'auth/user-not-found': 'errors.auth.invalidCredentials',
  'auth/email-already-in-use': 'errors.auth.emailInUse',
  'auth/weak-password': 'errors.auth.weakPassword',
  'auth/invalid-email': 'errors.auth.invalidEmail',
  'auth/too-many-requests': 'errors.rateLimit',
  'auth/network-request-failed': 'errors.network',
  'auth/user-token-expired': 'errors.sessionExpired',
  unauthenticated: 'errors.sessionExpired',
  'permission-denied': 'errors.permission',
  'storage/unauthorized': 'errors.permission',
  unavailable: 'errors.network',
};

/** HTTP-Status → kuratierter `common.json`-Key (`errors.*`), §1.1/§2.1. */
function resolveHttpStatusKey(status: number): string | null {
  switch (status) {
    case 401:
      return 'errors.sessionExpired';
    case 403:
      return 'errors.permission';
    case 404:
      return 'errors.notFound';
    case 409:
      return 'errors.conflict';
    case 400:
    case 422:
      return 'errors.validation';
    case 402:
      return 'errors.limitReached';
    case 429:
      return 'errors.rateLimit';
    default:
      return status >= 500 ? 'errors.server' : null;
  }
}

/** Fragmente bekannter Netzwerk-/Fetch-Fehlermeldungen (Stufe 3). */
const NETWORK_MESSAGE_FRAGMENTS = ['No response received', 'Failed to fetch', 'NetworkError'];

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const message = extractMessage(err);
  if (!message) return false;
  return NETWORK_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
}

function extractFirebaseCode(err: unknown): string | null {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

function extractHttpStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const response = (err as { response?: { status?: unknown } }).response;
  const status = response?.status;
  return typeof status === 'number' ? status : undefined;
}

function extractMessage(err: unknown): string | null {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    return typeof message === 'string' ? message : null;
  }
  return typeof err === 'string' ? err : null;
}

/**
 * Löst einen kuratierten Key IMMER im `common`-Namespace auf (Stufen 1-3+5).
 * Gibt `null` zurück, wenn i18next den Key nicht auflösen konnte (Missing-Key
 * -Fallback von i18next ist der Key selbst) — so kann der Aufrufer zur
 * nächsten Stufe weiterreichen statt versehentlich einen rohen Key-String
 * anzuzeigen.
 */
function resolveCommonKey(t: TFunction, key: string): string | null {
  const value = t(key, { ns: 'common' }) as unknown as string;
  if (typeof value !== 'string' || value.trim().length === 0 || value === key) return null;
  return value;
}

/**
 * Löst `opts.fallbackKey` (Stufe 4) auf. Versucht zuerst den Namespace, an
 * den der Aufrufer-`t` bereits gebunden ist (deckt bestehende, gute
 * Domänen-Keys ab, z.B. `'download.error'` in `posteingang.json` — §2.2 des
 * Plans: "keine Doppel-Anlage"), und fällt danach explizit auf den `common`
 * -Namespace zurück (deckt die neu kuratierten `errors.*`-Keys ab,
 * unabhängig davon ob der Aufrufer `common` in seiner Namespace-Liste hat).
 * Kein verlässliches automatisches Multi-NS-Fallback vorausgesetzt — der
 * bestehende Code prefixt an anderer Stelle bereits explizit `common:...`
 * (z.B. `InvoiceDashboard.tsx`), Verlass auf i18next-Array-Fallback wäre
 * daher ein ungetestetes Verhalten dieses Projekts.
 */
function resolveKey(t: TFunction, key: string): string | null {
  const direct = t(key) as unknown as string;
  if (typeof direct === 'string' && direct.trim().length > 0 && direct !== key) return direct;

  const viaCommon = t(key, { ns: 'common' }) as unknown as string;
  if (typeof viaCommon === 'string' && viaCommon.trim().length > 0 && viaCommon !== key) return viaCommon;

  return null;
}

export default mapErrorToUserMessage;
